import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AuditActivityId, AuditId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {untilDestroyed, UntilDestroy} from '@ngneat/until-destroy';
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../shared/types';
import {faChevronDown, faChevronUp} from '@fortawesome/pro-light-svg-icons';
import {BrowserSessionInfo, BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {WorkbenchViewComponent} from '../../shared/components/viewcontainers/workbench-view.component';
import {AssessmentTask, Audit, HydratedAuditTarget} from '@webmate-web-core/shared/services/audit.service';
import {
    faBan,
    faCheckCircle,
    faEllipsisV,
    faFilter,
    faFlagCheckered,
    faForward,
    faGlobe,
    faHandPaper,
    faHistory,
    faHourglass, faInfoCircle,
    faList,
    faMeh,
    faPlay,
    faPowerOff,
    faQuestionCircle,
    faRocketLaunch,
    faRunning,
    faSyncAlt,
    faTasks,
    faThumbsDown,
    faThumbsUp,
    faTv,
    faTimesCircle
} from '@fortawesome/pro-solid-svg-icons';
import {tagEquals, tagToString} from '@webmate-web-core/shared/types/tag';
import {AuditPlugin} from './audit-plugin';
import {WorkbenchAuditService} from '../../shared/services/workbench-audit.service';
import {AuditInfo, AuditSession} from '../../shared/services/audit-session';
import {WorkbenchCommandRegistry} from '../../shared/services/workbench-command-registry.service';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';
import {DeployableService} from '@webmate-web-core/shared/services/device/deployable.service';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {WorkbenchConsoleService} from '../../shared/services/workbench-console.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {filter, switchMap, take, timeout} from 'rxjs/operators';
import {TimeoutError} from 'rxjs';
import {INTERACTION_PATH_OPENER, InteractionPathOpener} from '@webmate-web-core/shared/services/interactionpath-opener.service';


@UntilDestroy()
@Component({
    templateUrl: './audit-navigator-view.component.html',
    styleUrls: ['./audit-navigator-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditNavigatorViewComponent extends WorkbenchViewComponent implements OnInit, OnDestroy {
    id: string;
    projectId: ProjectId;
    auditId?: AuditId;

    audit?: Audit;
    auditSession: AuditSession;
    auditInfo: AuditInfo;

    activeExpedition?: BrowserSessionInfo;
    activeActivity?: AuditActivityId;

    filteredTasks?: AssessmentTask[];

    states: { [stateId: string]: BrowserSessionStateArtifact } = {};

    taskAnimationStates: { [key: string]: boolean } = {};

    ///// FILTER
    filter: string = '';
    selectedSpecGroups: string[] = [];

    selectedTestObjects: string[] = [];

    selectedTags: string[] = [];

    selectedExecutionStatuses: string[] = [];

    selectedEvaluationStatuses: string[] = [];

    filtersActive: boolean = false;
    showFilters: boolean = false;
    ///// FILTER

    activitiesLoading: boolean = false;

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private workbenchLayoutManager: WorkbenchLayoutManager,
                private cdr: ChangeDetectorRef,
                layoutManager: WorkbenchLayoutManager,
                private workbenchAuditService: WorkbenchAuditService,
                private workbenchCommandRegistry: WorkbenchCommandRegistry,
                private readonly workbenchContextService: WorkbenchContextService,
                @Inject(INTERACTION_PATH_OPENER) private readonly interactionPathOpener: InteractionPathOpener,
                private readonly notificationService: WmNotificationService) {
        super(layoutManager, config);
    }

    ngOnInit() {
        // Check for current Context
        this.projectId = this.workbenchContextService.getCurrentWorkbenchContext()?.project!;
        this.auditId = this.workbenchContextService.getCurrentWorkbenchContext()?.auditContext?.activeAudit;

        // Subscribe to Context Switches
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(untilDestroyed(this)).subscribe(context => {
            if (context.auditContext?.activeAudit !== this.auditId) {
                this.auditId = context.auditContext?.activeAudit;
                this.auditInfo = undefined;
                this.filteredTasks = undefined;
                this.loadAuditSession();
            }
            this.activeExpedition = context.expeditionContext?.activeExpedition;
            this.activeActivity = context.auditContext?.activeAuditActivity?.id;
            if (this.activeActivity) {
                this.openActivity(this.activeActivity);
            }

            this.cdr.markForCheck();
        });

        // Subscribe to Audit Changes
        this.loadAuditSession();

        // Subscribe to Command for highlighting activity in list
        this.workbenchAuditService.focusActivityCommandChange$().pipe(untilDestroyed(this)).subscribe( activityID => {
            this.scrollToActivityElement(activityID);
        })
    }

    reloadActivities() {
        this.loadAuditSession();
    }

    loadAuditSession() {
        if (!this.auditId) {
            return;
        }

        this.activitiesLoading = true;
        // this.cdr.markForCheck();
        // todo try to replace with mark for check
        this.cdr.detectChanges();


        this.auditSession = this.workbenchAuditService.getOrCreateAuditSession(this.auditId);
        const auditId = this.auditId;
        this.auditSession.getAuditInfo$().pipe(
            filter(auditInfo => auditInfo.audit?.id === auditId),
            take(1),
            untilDestroyed(this)
        ).subscribe({
            next: (auditInfo) => {
                this.auditInfo = auditInfo;
                this.filteredTasks = auditInfo.assessmentTasks;
                this.filterChanged();
                this.activitiesLoading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Error loading audit session', err);
                this.activitiesLoading = false;
                this.cdr.markForCheck();
            },
            complete: () => {
                this.activitiesLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    private scrollToActivityElement(elementId: string): void {
        if(!this.viewId)
            return;

        // Make sure Panel is opened
        if(!this.workbenchLayoutManager.isViewContainerShown(AuditPlugin.ViewContainerId)){
            this.workbenchLayoutManager.toggleViewContainer(AuditPlugin.ViewContainerId);
        }

        // Get Scroll view of ViewContainer
        const scrollContainer = this.workbenchLayoutManager.viewContainerStates[AuditPlugin.ViewContainerId].getValue().componentRef.instance.getScrollView().nativeElement as HTMLElement;

        // Search for activity in List
        const activityElement = scrollContainer.querySelector(`#activity_${elementId}`) as HTMLElement;

        if(!activityElement)
            return;

        // Scroll to Element with an offset
        scrollContainer.scrollTop = activityElement.offsetTop - activityElement.clientHeight - 100;

        // Start Task Blinking Animation
        this.taskAnimationStates[elementId] = true;

        this.cdr.markForCheck();
    }

    // Gets called after finishing the Task Blinking animation after x iteration
    resetTaskBlinkingAnimation(taskID: string){
        this.taskAnimationStates[taskID] = false;
    }

    openActivity(activityId: AuditActivityId) {
        this.activeActivity = activityId;
        this.workbenchCommandRegistry.executeCommand("audit:openActivity", activityId);
    }

    toggleShowFilters() {
        this.showFilters = !this.showFilters;
    }

    ngOnDestroy() {
    }

    filterChanged() {
        const enrichedTasks = (this.auditInfo.assessmentTasks || []).map(task => {
            const assessedSpec = task.assessedSpec;
            const targetId = this.getTaskTargetId(task);
            task.numberOfFindings = targetId ? this.auditInfo.findingStatsBySpecIdAndTestObjectId?.[assessedSpec]?.[targetId]?.total || 0 : 0;
            return task;
        });

        this.filteredTasks = (enrichedTasks).filter(task => {
            const labels = (this.auditInfo.specs?.[task.assessedSpec]?.labels || []);
            const executionStatus = task.executionStatus;
            const evaluationStatus = task.evaluationStatus;
            const targetId = this.getTaskTargetId(task);
            let textToFilter = "";
            if (this.auditInfo.statesByTestObjectId) {
                const testObjectStateName = targetId ? this.auditInfo.statesByTestObjectId[targetId]?.name || 'unknown' : 'unknown';
                if (this.auditInfo.specs) {
                    textToFilter = testObjectStateName + ' ' + labels.map(tagToString).join(' ') + this.auditInfo.specs[task.assessedSpec]?.title;
                }
            }
            // TODO: spec
            return ((this.selectedTags || []).length === 0 || labels.some(label => this.selectedTags.find(selectedTag => tagEquals(selectedTag, label)))) &&
                ((this.selectedTestObjects || []).length === 0 || (!!targetId && this.selectedTestObjects.includes(targetId))) &&
                ((this.selectedExecutionStatuses || []).length === 0 || this.selectedExecutionStatuses.includes(executionStatus)) &&
                ((this.selectedEvaluationStatuses || []).length === 0 || this.selectedEvaluationStatuses.includes(evaluationStatus)) &&
                ((textToFilter.toLowerCase().includes(this.filter.toLowerCase())));
        });
        this.filtersActive = this.filter !== '' || this.selectedTags.length > 0 || this.selectedTestObjects.length > 0 || this.selectedExecutionStatuses.length > 0 || this.selectedEvaluationStatuses.length > 0;
        this.cdr.markForCheck();
    }

    getLabelForSelectedTags(): string {
        return this.selectedTags.length > 0 ? this.selectedTags.map(tagToString).join(', ') : 'All Tags';
    }

    getLabelForTag(tag: string): string {
        return tag;
    }

    getLabelForSelectedSpecGroups(): string {
        return this.selectedSpecGroups.length > 0 ? this.selectedSpecGroups.join(', ') : 'All Spec Groups';
    }

    getLabelForSelectedTestObjects(): string {
        return this.selectedTestObjects.length > 0 ? this.selectedTestObjects.join(', ') : 'All Test Objects';
    }

    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faGlobe = faGlobe;
    protected readonly faChevronDown = faChevronDown;
    protected readonly faChevronUp = faChevronUp;
    protected readonly faThumbsUp = faThumbsUp;
    protected readonly faThumbsDown = faThumbsDown;
    protected readonly faForward = faForward;
    protected readonly faRocketLaunch = faRocketLaunch;
    protected readonly faPlay = faPlay;
    protected readonly faMeh = faMeh;
    protected readonly faBan = faBan;
    protected readonly faList = faList;
    protected readonly faRunning = faRunning;
    protected readonly faFlagCheckered = faFlagCheckered;
    protected readonly faTasks = faTasks;
    protected readonly faHandPaper = faHandPaper;
    protected readonly faPowerOff = faPowerOff;
    protected readonly faEllipsisV = faEllipsisV;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly timesCircle = faTimesCircle;
    protected readonly tagToString = tagToString;
    protected readonly faFilter = faFilter;
    protected readonly faHistory = faHistory;
    protected readonly faHourglass = faHourglass;
    protected readonly faTv = faTv;

    getTaskTargetId(task?: AssessmentTask): string | undefined {
        return task?.testObjectId;
    }

    getTaskTarget(task?: AssessmentTask): HydratedAuditTarget | undefined {
        const targetId = this.getTaskTargetId(task);
        return targetId ? this.auditInfo.testObjectsById?.[targetId] : undefined;
    }

    getTaskTargetName(task?: AssessmentTask): string {
        const targetId = this.getTaskTargetId(task);
        return this.getTaskTarget(task)?.target?.name || (targetId ? this.auditInfo.statesByTestObjectId?.[targetId]?.name : undefined) || 'Unknown target';
    }

    canOpenTaskTarget(task?: AssessmentTask): boolean {
        const targetId = this.getTaskTargetId(task);
        return !!targetId && !!this.auditInfo.statesByTestObjectId?.[targetId]?.provenance?.path;
    }

    openInBrowser(task: AssessmentTask) {
        const targetId = this.getTaskTargetId(task);
        const interactionPath = targetId ? this.auditInfo.statesByTestObjectId?.[targetId]?.provenance?.path : undefined;
        if (!interactionPath) {
            return;
        }

        this.interactionPathOpener.open({interactionPath});
    }
}
